import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import ButtonPrimary from './ButtonPrimary';
import Button from './Button';
import { HiXMark } from 'react-icons/hi2';
import { TbCircleFilled } from 'react-icons/tb';
import { BiCheckbox, BiSolidCheckboxChecked } from "react-icons/bi";
import { vibrate } from '../../Utils/Helpers';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { ADDRESS_TYPE_HOME, ADDRESS_TYPE_LOCALE, ADDRESS_TYPE_NOT_LISTED, ADDRESS_TYPE_OTHER, ADDRESS_TYPE_SAVED, ADDRESS_TYPE_STORE_LIST, ADDRESS_TYPE_WORK, ALL_STATES, getIconAddress } from '../../Utils/Constants/Global';
import AddressService, { SearchProps } from '../../Services/AddressService';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import { IMaskInput } from 'react-imask';
import { formatCurrentAddress } from '../../Utils/Formatters';
import { useStoreContext } from '../../Contexts/StoreContext';
import ButtonClose from './ButtonClose';
import { useAddressHook } from '../../Hooks/AddressHook';
import { useTranslation } from 'react-i18next';
import { ORDER_TYPE_DELIVERY } from '../../Utils/Constants/Service';

interface FormAddressProps {
    nextOrderType: string | null;
    onCancel: () => void;
    onSuccess: (values: any) => void;
    onStoreNotFound: () => void;
}

const getValidationSchema = (t: any) =>
    Yup.object().shape({
        street: Yup.string().min(3, t("form.validation.invalid_min_characters", {quantity: 3})).required(t('form.validation.required')),
        number: Yup.string().when('no_address_number', {
            is: false,
            then: (schema) => schema.required(t('form.validation.required')),
            otherwise: (schema) => schema.notRequired(),
        }),
        complement: Yup.string().when('no_address_complement', {
            is: false,
            then: (schema) => schema.required(t('form.validation.required')),
            otherwise: (schema) => schema.notRequired(),
        }),
        reference: Yup.string(),
        neighborhood: Yup.string().required(t('form.validation.required')),
        zipcode: Yup.string().required(t('form.validation.required')),
        city: Yup.string().required(t('form.validation.required')),
        state: Yup.string().required(t('form.validation.required')),
        favoriteAddressOther: Yup.string().when('favoriteAddress', {
            is: 'Outro',
            then: (schema) => schema.required(t('form.validation.required')),
            otherwise: (schema) => schema.notRequired(),
        })
});

const FormAddress: React.FC<FormAddressProps> = ({ nextOrderType, onCancel, onSuccess, onStoreNotFound }) => {

    const { t } = useTranslation();
    const validationSchema = getValidationSchema(t);

    const FAVORITE_OPTIONS = [
        { label: t(ADDRESS_TYPE_HOME), value: ADDRESS_TYPE_HOME },
        { label: t(ADDRESS_TYPE_WORK), value: ADDRESS_TYPE_WORK },
        { label: t(ADDRESS_TYPE_OTHER), value: ADDRESS_TYPE_OTHER },
    ];

    const { states, setStates, isMobile, mainLoading, setMainLoading, setMainLoadingTitle, setMainLoadingMessage } = useLayoutContext();
    const { orderType, getOrderTypeIdByName } = useOrderTypeContext();
    const { nextCurrentAddress, setNextCurrentAddress } = useStoreContext();
    const { search } = useAddressHook();

    useEffect(() => {
        setStates(ALL_STATES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const INVALID_ADDRESSES_TYPES = [
        ADDRESS_TYPE_LOCALE,
        ADDRESS_TYPE_SAVED,
        ADDRESS_TYPE_NOT_LISTED,
        ADDRESS_TYPE_STORE_LIST,
    ];

    const checkInitialFavoriteAddress = (): string => {
        const title = nextCurrentAddress?.title;
        return (!title || INVALID_ADDRESSES_TYPES.includes(title)) ? ADDRESS_TYPE_HOME : title;
    };

    const [initialValues, setInitialValues] = useState({
        street: nextCurrentAddress?.street || '',
        number: nextCurrentAddress?.number || '',
        complement: nextCurrentAddress?.complement || '',
        reference: '',
        neighborhood: nextCurrentAddress?.neighborhood || '',
        zipcode: nextCurrentAddress?.zipcode,
        city: nextCurrentAddress?.city || '',
        state: nextCurrentAddress?.state || '',
        no_address_number: false,
        no_address_complement: nextOrderType !== ORDER_TYPE_DELIVERY, //Marcando como false para não ser obrigatório no delivery
        favoriteAddress: checkInitialFavoriteAddress(),
        favoriteAddressOther: '',
        lat: nextCurrentAddress?.lat,
        lng: nextCurrentAddress?.lng,
    });

    const [valuesFromSearch] = useState(initialValues);

    const isFieldRequired = (schema: any, fieldName: string) => {
        const field = schema.describe().fields[fieldName];
        return field.tests.some((test: any) => test.name === 'required');
    };

    const handleSearch = async (params: SearchProps) => {

        setTimeout(() => {
            setMainLoading(true);
            setMainLoadingTitle(t('start.form_address.button_submit.loading'));
            setMainLoadingMessage(t('layout.loading_main.default_message'));
        }, 0);

        search(params)
            .then((response) => {
                onSuccess(response)
            })
            .catch((error: any) => {
                setMainLoading(false);
                if (error?.response?.data?.error === 'no_stores_found') {
                    onStoreNotFound();
                }
            })
    }

    const searchComponents = async (text: string) => {

        setMainLoading(true); //OK
        setMainLoadingTitle(t('start.form_address.button_submit.loading'));
        setMainLoadingMessage(t('layout.loading_main.default_message'));

        try {

            const addressSevice = new AddressService(orderType);
            const getComponents = await addressSevice.components(text);
            return getComponents;

        } catch (error: any) {
            setMainLoading(false);
            throw error;
        }
    }

    const handleNextCurrentAddress = (values: any) => {
        setInitialValues(values);
        setNextCurrentAddress(values);
    }

    const [pendingSearch, setPendingSearch] = useState<any>(null);

    useEffect(() => {
        if (pendingSearch && nextCurrentAddress?.lat === pendingSearch.lat && nextCurrentAddress?.lng === pendingSearch.lng) {
            // quando o endereço for realmente atualizado, executa a busca
            handleSearch(pendingSearch);
            setPendingSearch(null); // limpa o estado temporário
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nextCurrentAddress]);

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => {
                const textSearchComponents = `${values.street} ${values.number} ${values.state} ${values.city}`;

                setMainLoading(true);
                setMainLoadingTitle(t('start.form_address.button_submit.loading'));
                setMainLoadingMessage(t('layout.loading_main.default_message'));

                searchComponents(textSearchComponents)
                    .then((response: any) => {
                        const searchParams = {
                            number: values.number,
                            zip_code: values.zipcode.replace(/\D/g, ""),
                            lat: response.location.lat,
                            lng: response.location.lng,
                            order_type: nextOrderType ? getOrderTypeIdByName(nextOrderType) : '',
                        };
                        setPendingSearch(searchParams);
                        setNextCurrentAddress((prev: any) => ({
                            ...prev,
                            lat: response.location.lat,
                            lng: response.location.lng,
                            initial_pin: {
                                lat: response.location.lat,
                                lng: response.location.lng,
                            },
                            default: true
                        }));
                    })
                    .catch(() => {
                        setMainLoading(false);
                    });
            }}
        >
            {({ values, setFieldValue, handleBlur }) => {

                // eslint-disable-next-line react-hooks/rules-of-hooks
                useEffect(() => {
                    if (values.favoriteAddress === ADDRESS_TYPE_OTHER) {
                        const element = document.getElementById("contentToScrollOther");
                        element?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                    handleNextCurrentAddress(values);
                }, [values]);

                return (
                    <Form className="flex flex-col justify-between h-full p-4 pt-2 pb-0">

                        <div className='flex flex-col justify-between gap-2 w-full px-0.5 relative'>

                            <div className="grid gap-1.5 leading-4 z-10 px-0.5 pb-3">
                                <span className="flex items-center gap-1 text-lg">
                                    <span className='-ml-0.5 leading-none'>{getIconAddress(values.favoriteAddress, 22)}</span>
                                    <span className='font-bold leading-none'>{t(values.favoriteAddressOther ? values.favoriteAddressOther : values.favoriteAddress)}</span>
                                </span>
                                <span className='text-gray-500 leading-5'>
                                    {formatCurrentAddress(values)}
                                </span>
                            </div>

                            <div className='flex flex-col gap-4 w-full h-full px-0.5'>
                                <div className='flex flex-col gap-3'>
                                    {/* Endereço */}
                                    {!valuesFromSearch?.street &&
                                        <div className='grid gap-3'>
                                            <div className='w-full'>
                                                <span className='text-sm font-medium text-shopfood-title'>
                                                    {t('start.form_address.street.label')}
                                                    {isFieldRequired(validationSchema, 'street') && (
                                                        <sup className="text-shopfood-error inline-flex ml-0.5">
                                                            <TbCircleFilled size={7} />
                                                        </sup>
                                                    )}
                                                </span>
                                                <Field
                                                    name="street"
                                                    onBlur={handleBlur}
                                                    value={values.street}
                                                    disabled={mainLoading}
                                                    placeholder={t('start.form_address.street.placeholder')}
                                                    className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 placeholder:font-normal placeholder:normal-case font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 disabled:bg-gray-100 disabled:text-gray-400"
                                                />
                                                <ErrorMessage name="street" component="div" className='text-xs text-shopfood-error pt-1' />
                                            </div>
                                        </div>
                                    }

                                    <div className='w-full flex flex-col justify-between gap-3'>

                                        {/* Número */}
                                        <div className='w-full'>
                                            <span className='text-sm font-medium text-shopfood-title'>
                                                {t('start.form_address.number.label')}
                                                {isFieldRequired(validationSchema, 'number') && (
                                                    <sup className="text-shopfood-error inline-flex ml-0.5">
                                                    <TbCircleFilled size={7} />
                                                    </sup>
                                                )}
                                            </span>
                                            <div className="relative w-full flex items-center justify-between">
                                                <Field
                                                    name="number"
                                                    type={isMobile ? 'number' : 'text'}
                                                    value={values.number}
                                                    disabled={values.no_address_number || mainLoading}
                                                    onBlur={handleBlur}
                                                    placeholder={values.no_address_number ? t('start.form_address.number.no_number_label') : ''}
                                                    className="w-full block rounded border-0 p-2.5 pr-14 font-extrabold text-lg ring-1 ring-gray-300 placeholder:text-gray-300 focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:font-normal"
                                                />

                                                {(values.number) &&
                                                    <div className='absolute flex items-center right-0 h-11 font-medium text-shopfood-title text-xs w-12 justify-center'>
                                                        <ButtonClose onClick={() => setFieldValue('number', '')} className='right-3' />
                                                    </div>
                                                }
                                                {!values.number &&
                                                    <div
                                                        className={`
                                                            absolute flex items-center right-0 h-full font-medium text-shopfood-notify text-sm px-2 text-nowrap justify-center
                                                            rounded-r cursor-pointer
                                                            border-gray-300
                                                            ${values.no_address_number ? 'bg-gray-100' : 'bg-gray-50 border-l'}
                                                        `}
                                                        onClick={() => {
                                                            const newValue = !values.no_address_number;
                                                            setFieldValue('no_address_number', newValue);
                                                            setFieldValue('number', newValue ? '' : '');
                                                            vibrate();
                                                        }}
                                                        >
                                                        { values.no_address_number
                                                            ? <BiSolidCheckboxChecked size={21} className='text-custom-primary flex items-center' />
                                                            : <BiCheckbox size={21} className='flex items-center' />
                                                        }
                                                        <span className={`cursor-pointer font-medium flex items-center h-11 ${values.no_address_number ? 'text-custom-primary' : ''}`}>
                                                            {t('start.form_address.number.no_number_label')}
                                                        </span>
                                                    </div>
                                                }
                                            </div>
                                            <ErrorMessage name="number" component="div" className='text-xs text-shopfood-error pt-1' />
                                        </div>

                                        {/* Complemento */}
                                        <div className='w-full'>
                                            <span className='text-sm font-medium text-shopfood-title'>
                                                {t('start.form_address.complement.label')}
                                                {isFieldRequired(validationSchema, 'complement') && (
                                                    <sup className="text-shopfood-error inline-flex ml-0.5">
                                                    <TbCircleFilled size={7} />
                                                </sup>
                                            )}
                                            </span>
                                            <div className="relative w-full flex items-center justify-between">
                                                <Field
                                                    name="complement"
                                                    value={values.complement}
                                                    disabled={values.no_address_complement || mainLoading}
                                                    onBlur={handleBlur}
                                                    placeholder={values.no_address_complement ? t('start.form_address.complement.no_complement_label') : t('start.form_address.complement.placeholder')}
                                                    className="w-full block rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 placeholder:font-normal font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:font-normal"
                                                />

                                                {(values.complement) &&
                                                    <div className='absolute flex items-center right-0 h-11 font-medium text-shopfood-title text-sm w-12 justify-center'>
                                                        <ButtonClose onClick={() => setFieldValue('complement', '')} className='right-3' />
                                                    </div>
                                                }
                                                {!values.complement &&
                                                    <div
                                                        className={`
                                                            absolute flex items-center right-0 h-full font-medium text-shopfood-notify text-sm px-2 text-nowrap justify-center
                                                            rounded-r cursor-pointer
                                                            border-gray-300
                                                            ${values.no_address_complement ? 'bg-gray-100' : 'bg-gray-50 border-l'}
                                                        `}
                                                        onClick={() => {
                                                            const newValue = !values.no_address_complement;
                                                            setFieldValue('no_address_complement', newValue);
                                                            setFieldValue('complement', newValue ? '' : '');
                                                            vibrate();
                                                        }}
                                                        >
                                                        { values.no_address_complement
                                                            ? <BiSolidCheckboxChecked size={21} className='text-custom-primary flex items-center' />
                                                            : <BiCheckbox size={21} className='flex items-center' />
                                                        }
                                                        <span className={`cursor-pointer font-medium flex items-center h-11 ${values.no_address_complement ? 'text-custom-primary' : ''}`}>
                                                            {t('start.form_address.complement.no_complement_label')}
                                                        </span>
                                                    </div>
                                                }
                                            </div>
                                            <ErrorMessage name="complement" component="div" className='text-xs text-shopfood-error pt-1' />
                                        </div>

                                    </div>

                                    {/* Referência */}
                                    <div className='w-full'>
                                        <span className='text-sm font-medium text-shopfood-title'>
                                            {t('start.form_address.reference.label')}
                                        </span>
                                        <Field
                                            name="reference"
                                            value={values.reference}
                                            disabled={mainLoading}
                                            onBlur={handleBlur}
                                            placeholder={t('start.form_address.reference.placeholder')}
                                            className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 placeholder:font-normal font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 disabled:bg-gray-100 disabled:text-gray-400"
                                        />
                                        <ErrorMessage name="reference" component="div" className='text-xs text-shopfood-error pt-1' />
                                    </div>

                                    <div className='w-full flex justify-between gap-3'>
                                        {/* Bairro */}
                                        {!valuesFromSearch?.neighborhood &&
                                            <div className='w-full'>
                                                <span className='text-sm font-medium text-shopfood-title'>
                                                    {t('start.form_address.neighborhood.label')}
                                                    {isFieldRequired(validationSchema, 'neighborhood') && (
                                                        <sup className="text-shopfood-error inline-flex ml-0.5">
                                                            <TbCircleFilled size={7} />
                                                        </sup>
                                                    )}
                                                </span>
                                                <Field
                                                    name="neighborhood"
                                                    value={values.neighborhood}
                                                    disabled={mainLoading}
                                                    onBlur={handleBlur}
                                                    placeholder={t('start.form_address.neighborhood.placeholder')}
                                                    className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 placeholder:font-normal font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 disabled:bg-gray-100 disabled:text-gray-400"
                                                />
                                                <ErrorMessage name="neighborhood" component="div" className='text-xs text-shopfood-error pt-1' />
                                            </div>
                                        }


                                        {/* CEP */}
                                        {!valuesFromSearch?.zipcode &&
                                            <div className='w-52'>
                                                <span className='text-sm font-medium text-shopfood-title'>
                                                    {t('start.form_address.zipcode.label')}
                                                    {isFieldRequired(validationSchema, 'zipcode') && (
                                                        <sup className="text-shopfood-error inline-flex ml-0.5">
                                                            <TbCircleFilled size={7} />
                                                        </sup>
                                                    )}
                                                </span>
                                                <Field name="zipcode">
                                                    {({ field, form }: any) => (
                                                        <IMaskInput
                                                            {...field}
                                                            mask="00000-000"
                                                            unmask={"typed"}
                                                            value={field.value}
                                                            onAccept={(value: string) => form.setFieldValue(field.name, value)}
                                                            onBlur={handleBlur}
                                                            disabled={mainLoading}
                                                            placeholder={t('start.form_address.zipcode.placeholder')}
                                                            className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 placeholder:font-normal font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 disabled:bg-gray-100 disabled:text-gray-400"
                                                        />
                                                    )}
                                                </Field>
                                                <ErrorMessage name="zipcode" component="div" className='text-xs text-shopfood-error pt-1' />
                                            </div>
                                        }
                                    </div>



                                    {/* Cidade e Estado */}
                                    {(!valuesFromSearch?.city && !valuesFromSearch?.state) &&
                                        <div className='w-full flex justify-between gap-3'>
                                            <div className='w-full self-start'>
                                                <span className='text-sm font-medium text-shopfood-title'>
                                                    {t('start.form_address.city.label')}
                                                    {isFieldRequired(validationSchema, 'city') && (
                                                        <sup className="text-shopfood-error inline-flex ml-0.5">
                                                            <TbCircleFilled size={7} />
                                                        </sup>
                                                    )}
                                                </span>
                                                <Field
                                                    name="city"
                                                    value={values.city}
                                                    onBlur={handleBlur}
                                                    placeholder={t('start.form_address.city.placeholder')}
                                                    disabled={mainLoading}
                                                    className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 placeholder:font-normal font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 disabled:bg-gray-100 disabled:text-gray-400"
                                                />
                                                <ErrorMessage name="city" component="div" className='text-xs text-shopfood-error pt-1' />
                                            </div>

                                            <div className='w-52 self-start'>
                                                <span className='text-sm font-medium text-shopfood-title'>
                                                    {t('start.form_address.state.label')}
                                                    {isFieldRequired(validationSchema, 'state') && (
                                                        <sup className="text-shopfood-error inline-flex ml-0.5">
                                                            <TbCircleFilled size={7} />
                                                        </sup>
                                                    )}
                                                </span>
                                                <Field
                                                    as="select"
                                                    name="state"
                                                    value={values.state}
                                                    disabled={mainLoading}
                                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                                        setFieldValue('state', e.target.value)
                                                    }
                                                    className="p-2.5 py-3 ring-1 ring-gray-300 bg-white rounded w-full h-full font-bold disabled:bg-gray-100"
                                                    >
                                                    <option value="">---</option>
                                                    {states.map((uf: string) => (
                                                        <option key={uf} value={uf}>{uf}</option>
                                                    ))}
                                                </Field>
                                                <ErrorMessage name="state" component="div" className='text-xs text-shopfood-error pt-1' />
                                            </div>
                                        </div>
                                    }

                                </div>

                                {/* Favorito */}
                                <div className="grid gap-4 md:gap-4 pt-2 pb-6">
                                    <div className="flex items-center justify-evenly gap-1">
                                        <hr className="w-full opacity-50" />
                                        <h4 className="text-sm font-medium text-shopfood-title text-center text-nowrap leading-none flex items-center gap-1">{t('start.form_address.favorite_address.title')}</h4>
                                        <hr className="w-full opacity-50" />
                                    </div>

                                    <div className="flex gap-2 items-center justify-between">
                                        { FAVORITE_OPTIONS.map(({ label, value }) => (
                                        values.favoriteAddress === value
                                            ? <ButtonPrimary disabled={mainLoading} outline key={value} onClick={() => setFieldValue('favoriteAddress', value)} className="w-full gap-0.5">
                                                {getIconAddress(value, 17)} {t(`start.form_address.favorite_address.${label}`)}
                                            </ButtonPrimary>
                                            : <Button key={value} disabled={mainLoading} onClick={() => {
                                                setFieldValue('favoriteAddress', value);
                                                vibrate();
                                            }} className="w-full gap-0.5">
                                                {getIconAddress(value, 17)}
                                                {t(`start.form_address.favorite_address.${label}`)}
                                            </Button>
                                        ))}
                                    </div>

                                    {values.favoriteAddress === ADDRESS_TYPE_OTHER && (
                                        <div className='w-full' id="contentToScrollOther">
                                            <span className='text-sm font-medium text-shopfood-title'>{t('start.form_address.favorite_address.label')}</span>
                                            <Field
                                                name="favoriteAddressOther"
                                                value={values.favoriteAddressOther}
                                                onBlur={handleBlur}
                                                    disabled={mainLoading}
                                                placeholder={t('start.form_address.favorite_address.placeholder')}
                                                className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 placeholder:font-normal placeholder:normal-case font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                            />
                                            <ErrorMessage name="favoriteAddressOther" component="div" className='text-xs text-shopfood-error pt-1' />
                                        </div>
                                    )}
                                </div>

                            </div>

                        </div>

                        {/* Botões */}
                        <div className='flex gap-2 sticky bottom-0 py-4 w-full bg-white/65 backdrop-blur-sm'>
                            <ButtonPrimary
                                type='submit'
                                disabled={mainLoading}
                                className='w-full'
                                onClick={() => {}}
                            >
                                    {t('start.form_address.button_submit.label')}
                            </ButtonPrimary>
                            <Button rounded onClick={onCancel}>
                                <span><HiXMark size={20} /></span>
                            </Button>
                        </div>
                    </Form>
            )}}
        </Formik>
        );
};

export default FormAddress;
